import { Component, Input, OnInit, ViewChild } from '@angular/core';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { MatTableDataSource } from '@angular/material/table';
import { ModalController } from '@ionic/angular';
import { finalize, take } from 'rxjs';
import { LoadingService } from 'src/app/modules/shared/components/loading/loading.service';
import { DateUtilsService } from 'src/app/modules/shared/services/date-utils/date-utils.service';
import { ToastService } from 'src/app/modules/shared/services/toast/toast.service';
import { Router } from '@angular/router';

@Component({
  selector: 'app-modal-historic',
  templateUrl: './modal-historic.component.html',
  styleUrls: ['./modal-historic.component.scss'],
})
export class ModalHistoricComponent implements OnInit {
  @Input() fieldMappings!: { [key: string]: string };
  @Input() service!: any;
  @Input() id!: string;
  @Input() consultaDoisParam: boolean = false;
  @Input() idSecundario!: string;
  @Input() newTabOpenLink: boolean = false;
  @Input() customGetHistoricMethod: any = 'getHistorico';
  dataSource = new MatTableDataSource<any>([]);

  pageSize: number = 10;
  pageIndex: number = 0;
  totalPages: number = 1;
  totalElements: number = 0;

  @ViewChild(MatPaginator, { static: false }) paginator!: MatPaginator;

  historico: any[] = [];

  constructor(
    private dateUtils: DateUtilsService,
    private toastService: ToastService,
    private loadingService: LoadingService,
    private modalCtrl: ModalController,
    private router: Router,
  ) {}

  ngOnInit() {
    this.dataSource.paginator = this.paginator;
    this.consultaDoisParam ? this.loadDataDoisParam(0) : this.loadData(0);
  }

  loadData(pageIndex: number) {
    this.loadingService.showLoading();
    this.service[this.customGetHistoricMethod](this.id, pageIndex)
      .pipe(
        take(1),
        finalize(() => {
          this.loadingService.hideLoading();
        }),
      )
      .subscribe(
        (data: any) => {
          const { content, pageSize, totalPages, totalElements, pageNumber } =
            data;

          const processedContent = content.map((item: any) => {
            return {
              ...item,
              alteracoes: item.alteracoes.map((alteracao: any) => {
                return {
                  ...alteracao,
                  colunaFormatada:
                    this.fieldMappings[alteracao.coluna] ||
                    this.rotuloPorta(alteracao.coluna) ||
                    this.humanizeField(alteracao.coluna),
                };
              }),
            };
          });

          this.dataSource.data = processedContent;
          this.pageSize = pageSize;
          this.totalPages = totalPages;
          this.totalElements = totalElements;
          this.pageIndex = pageNumber;
        },
        (erro: any) => {
          if (erro.status !== 500 && erro.status) {
            this.toastService.showError(erro.error.message);
          } else {
            this.toastService.showError();
          }
        },
      );
  }

  loadDataDoisParam(pageIndex: number) {

    if (!this.idSecundario || this.idSecundario == '0') return;

    this.loadingService.showLoading();
    this.service[this.customGetHistoricMethod](
      this.id,
      this.idSecundario,
      pageIndex,
    )
      .pipe(
        take(1),
        finalize(() => {
          this.loadingService.hideLoading();
        }),
      )
      .subscribe(
        (data: any) => {
          const { content, pageSize, totalPages, totalElements, pageNumber } =
            data;

          const processedContent = content.map((item: any) => {
            return {
              ...item,
              alteracoes: item.alteracoes.map((alteracao: any) => {
                return {
                  ...alteracao,
                  colunaFormatada:
                    this.fieldMappings[alteracao.coluna] ||
                    this.rotuloPorta(alteracao.coluna) ||
                    this.humanizeField(alteracao.coluna),
                };
              }),
            };
          });

          this.dataSource.data = processedContent;
          this.pageSize = pageSize;
          this.totalPages = totalPages;
          this.totalElements = totalElements;
          this.pageIndex = pageNumber;
        },
        (erro: any) => {
          if (erro.status !== 500 && erro.status) {
            this.toastService.showError(erro.error.message);
          } else {
            this.toastService.showError();
          }
        },
      );
  }

  onPageChange(newPage: number) {
    this.consultaDoisParam
      ? this.loadDataDoisParam(newPage)
      : this.loadData(newPage);
  }

  pageChangeEvent(event: PageEvent): void {
    this.consultaDoisParam
      ? this.loadDataDoisParam(event.pageIndex)
      : this.loadData(event.pageIndex);
  }

  formatDateToReadable(isoDate: string): string {
    return this.dateUtils.formatDateToReadable(isoDate);
  }

  /**
   * PBI 110690 (§4.4 do contrato): colunas dinâmicas das medidas por porta —
   * `portaDireita3` → "Porta Direita 3". Sem isto, o `humanizeField` daria
   * "Porta direita3". Vale para todas as telas que abrem este modal.
   */
  private rotuloPorta(coluna: string): string | null {
    const match = /^porta(Direita|Esquerda)(\d+)$/.exec(coluna ?? '');
    return match ? `Porta ${match[1]} ${match[2]}` : null;
  }

  /**
   * Fallback para colunas sem rótulo mapeado em `fieldMappings`: transforma a chave crua
   * (camelCase/PascalCase, snake_case ou kebab-case) em texto legível, com espaços e apenas
   * a primeira letra maiúscula. Ex.: "criadoEm" → "Criado em", "BloqueioEm" → "Bloqueio em".
   */
  private humanizeField(field: string): string {
    if (!field) return field;
    const comEspacos = field
      .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
      .replace(/[_-]+/g, ' ')
      .trim();
    const minusculo = comEspacos.toLowerCase();
    return minusculo.charAt(0).toUpperCase() + minusculo.slice(1);
  }

  cancel() {
    return this.modalCtrl.dismiss(null, 'cancel');
  }

  confirm() {
    return this.modalCtrl.dismiss(null, 'confirm');
  }

  //#region Utils
  clickLinkNewOrSame(url: string) {
    this.newTabOpenLink ? this.buildHashLink(url) : this.navigateFromRaw(url);
  }

  buildHashLink(rawUrl: string): void {
    if (rawUrl) {
      let url = rawUrl
        .replace(/['"]/g, '')
        .replace(/^\.\.\//, '')
        .trim();

      const segments = url.split('/');

      const tree = this.router.createUrlTree(segments);

      const serialized = this.router.serializeUrl(tree);

      window.open(`${window.location.origin}/#${serialized}`, '_blank');
    }
  }

  navigateFromRaw(rawUrl: string): void {
    if (!rawUrl) return;

    let url = rawUrl
      .replace(/['"]/g, '')
      .replace(/^\.\.\//, '')
      .trim();

    const segments = url.split('/');

    this.router.navigate(segments);
    this.cancel();
  }
  //#endregion
}
